/**
 * Table utility functions for battery map rendering
 * Provides reusable logic for table construction, unit placement, and alignment
 */

// Unit shape constants
export const UNIT_SHAPE = {
	SQUARE: 0,
	HORIZONTAL: 1,
	VERTICAL: 2
};

// Rotation constants
export const ROTATION = {
	NONE: 0,
	TOP_LEFT: 1,
	TOP_RIGHT: 2,
	BOTTOM_LEFT: 3,
	BOTTOM_RIGHT: 4
};

/**
 * Initialize an empty table grid
 * @param {number} rows - Number of rows
 * @param {number} columns - Number of columns
 * @returns {Array} - 2D array filled with null
 */
export const createEmptyTable = (rows, columns) => {
	const table = [];
	for (let i = 0; i < rows; i++) {
		table[i] = new Array(columns).fill(null);
	}
	return table;
};

/**
 * Alignment configuration based on rotation value
 * @param {number} rotation - Rotation value (0-4)
 * @returns {Object} - Vertical and horizontal alignment
 */
export const getAlignment = (rotation) => {
	switch (rotation) {
		case ROTATION.TOP_LEFT:
			return { vertical: 'top', horizontal: 'left' };
		case ROTATION.TOP_RIGHT:
			return { vertical: 'top', horizontal: 'right' };
		case ROTATION.BOTTOM_LEFT:
			return { vertical: 'bottom', horizontal: 'left' };
		case ROTATION.BOTTOM_RIGHT:
			return { vertical: 'bottom', horizontal: 'right' };
		default:
			return { vertical: 'center', horizontal: 'center' };
	}
};

/**
 * Transform unit shape based on rotation
 * @param {number} originalShape - Original unit shape (0=square, 1=horizontal, 2=vertical)
 * @param {number} rotation - Rotation value
 * @returns {number} - Transformed shape
 */
export const transformUnitShape = (originalShape, rotation) => {
	// Square units are unaffected by rotation
	if (originalShape === UNIT_SHAPE.SQUARE || rotation === ROTATION.NONE) {
		return originalShape;
	}
	
	// Horizontal becomes vertical, vertical becomes horizontal
	return originalShape === UNIT_SHAPE.HORIZONTAL ? UNIT_SHAPE.VERTICAL : UNIT_SHAPE.HORIZONTAL;
};

/**
 * Determine if the table needs to be expanded
 * @param {Array} units - Array of unit objects
 * @param {number} unitShape - Base unit shape
 * @param {number} numberColumns - Current number of columns
 * @param {number} numberRows - Current number of rows
 * @returns {Object} - Expansion requirements
 */
export const calculateTableExpansion = (units, unitShape, numberColumns, numberRows) => {
	let needsTopRow = false;
	let needsRightColumn = false;

	units.forEach(unit => {
		const rotation = unit.unit_rotation || ROTATION.NONE;
		
		// Skip square units or units with no rotation
		if (unitShape === UNIT_SHAPE.SQUARE || rotation === ROTATION.NONE) return;

		// Check if horizontal unit needs top row
		if (unitShape === UNIT_SHAPE.HORIZONTAL && rotation > ROTATION.NONE && unit.unit_position_row === 0) {
			needsTopRow = true;
		}

		// Check if vertical unit needs right column
		if (unitShape === UNIT_SHAPE.VERTICAL && rotation > ROTATION.NONE && unit.unit_position_col === numberColumns - 1) {
			needsRightColumn = true;
		}
	});

	return { needsTopRow, needsRightColumn };
};

// Calculate cell spanning for a unit
export const calculateCellSpan = ({
	originalShape,
	rotation,
	row,
	col,
	actualRows,
	actualColumns,
	occupied
}) => {
	let rowspan = 1;
	let colspan = 1;
	let startRow = row;
	let startCol = col;

	// Only apply spanning if there's rotation or Square battery shape
	if (rotation === ROTATION.NONE || originalShape === UNIT_SHAPE.SQUARE) {
		return { rowspan, colspan, startRow, startCol };
	}

	// Horizontal units with rotation become vertical and span upward
	if (originalShape === UNIT_SHAPE.HORIZONTAL) {
		const targetRow = row - 1;
		if (targetRow >= 0 && !occupied[targetRow][col]) {
			rowspan = 2;
			startRow = targetRow;
		}
	} 
	// Vertical units with rotation become horizontal and span rightward
	else if (originalShape === UNIT_SHAPE.VERTICAL) {
		const targetCol = col + 1;
		if (targetCol < actualColumns && !occupied[row][targetCol]) {
			colspan = 2;
		}
	}

	return { rowspan, colspan, startRow, startCol };
};

/**
 * Mark cells as occupied in the occupancy grid
 * @param {Array} occupied - 2D occupancy grid
 * @param {number} row - Starting row
 * @param {number} col - Starting column
 * @param {number} rowspan - Number of rows to span
 * @param {number} colspan - Number of columns to span
 */
export const markCellsOccupied = (occupied, row, col, rowspan, colspan) => {
	for (let r = row; r < row + rowspan; r++) {
		for (let c = col; c < col + colspan; c++) {
			occupied[r][c] = true;
		}
	}
};

/**
 * Create a unit object for table placement
 * @param {Object} unit - Unit data from API
 * @param {Object} params - Additional parameters
 * @returns {Object} - Unit object for rendering
 */
export const createUnitObject = (unit, { shape, rowspan, colspan, rotation }) => {
	return {
		unit_number: unit.unit_number,
		unit_shape: shape,
		unit_status: unit.status,
		unit_dates: {
			install: unit.install_date,
			manufacture: unit.manufacture_date,
			warranty_expiration: unit.warranty_expiration_date,
		},
		unit_comments: unit.unit_comments,
		rowspan,
		colspan,
		alignment: getAlignment(rotation)
	};
};

/**
 * Mark spanned cells in the table
 * @param {Array} table - 2D table array
 * @param {number} row - Starting row
 * @param {number} col - Starting column
 * @param {number} rowspan - Number of rows to span
 * @param {number} colspan - Number of columns to span
 */
export const markSpannedCells = (table, row, col, rowspan, colspan) => {
	for (let r = row; r < row + rowspan; r++) {
		for (let c = col; c < col + colspan; c++) {
			if (r !== row || c !== col) {
				table[r][c] = { isSpanned: true };
			}
		}
	}
};

/**
 * Build a complete unit table with rotation and spanning logic
 * @param {Object} params - Table building parameters
 * @returns {Array} - 2D array representing the table
 */
export const buildUnitTable = ({ unitShape, numberColumns, numberRows, units }) => {
	// If dimensions are missing, return empty table
	if (!numberColumns || !numberRows) {
		return [];
	}

	// If no units, return an initialized empty table with correct dimensions
	if (!units || units.length === 0) {
		return createEmptyTable(numberRows, numberColumns);
	}

	// Calculate table expansion needs
	const { needsTopRow, needsRightColumn } = calculateTableExpansion(
		units, 
		unitShape, 
		numberColumns, 
		numberRows
	);

	// Calculate actual table dimensions
	const actualRows = needsTopRow ? numberRows + 1 : numberRows;
	const actualColumns = needsRightColumn ? numberColumns + 1 : numberColumns;
	const rowOffset = needsTopRow ? 1 : 0;

	// Initialize table and occupancy grid
	const table = createEmptyTable(actualRows, actualColumns);
	const occupied = Array(actualRows).fill(null).map(() => Array(actualColumns).fill(false));

	// Place each unit in the table
	units.forEach(unit => {
		const rotation = unit.unit_rotation || 0;
		const shape = transformUnitShape(unitShape, rotation);
		
		// Calculate base position with offset
		const baseRow = unit.unit_position_row + rowOffset;
		const baseCol = unit.unit_position_col;

		// Calculate spanning
		const { rowspan, colspan, startRow, startCol } = calculateCellSpan({
			originalShape: unitShape,
			rotation,
			row: baseRow,
			col: baseCol,
			actualRows,
			actualColumns,
			occupied
		});

		// Create unit object
		const unitObj = createUnitObject(unit, { shape, rowspan, colspan, rotation });

		// Mark cells as occupied
		markCellsOccupied(occupied, startRow, startCol, rowspan, colspan);

		// Place unit in table
		table[startRow][startCol] = unitObj;
		
		// Mark spanned cells
		markSpannedCells(table, startRow, startCol, rowspan, colspan);
	});

	return table;
};
